import React, { useMemo } from 'react'
import { Col, Row } from 'react-bootstrap'
import { Link } from 'react-router-dom'

import { formatDate, getUserDisplayName } from '../../../apps/professional-app/fa/common/aifeatures/utils'

import { selectProcessDocumentContact } from '../../../store/processDocument/documentUsers'
import { useAppSelector } from '../../../store/hooks'
import {
  selectDocumentIsNotRelatedToClient,
  selectDocumentLabel,
  selectDocumentSummaryExecutionLoading,
  selectDocumentType,
  selectSelectedDocumentType,
} from '../../../store/processDocument/documentSummary'

import { FALLBACK_DOCUMENT_TYPE_ID } from '../../../models/DocumentType'

import { Button } from '../../../components/Button/Button'

import { DocumentTypeSelectDropdown } from '../DocumentTypeSelectDropdown/DocumentTypeSelectDropdown'

import { DocumentSummaryContacts } from './DocumentSummaryContacts'
import { DocumentSummaryEditor } from './DocumentSummaryEditor'
import { DocumentSummaryChat } from './DocumentSummaryChat'
import { useDocumentSummary } from './hooks'

import './DocumentSummary.scss'

export const DocumentSummary = () => {
  const documentType = useAppSelector(selectDocumentType)
  const contact = useAppSelector(selectProcessDocumentContact)
  const documentLabel = useAppSelector(selectDocumentLabel)
  const documentIsNotRelatedToClient = useAppSelector(selectDocumentIsNotRelatedToClient)

  const currentDateTime = useMemo(() => formatDate(new Date().toISOString()), [])

  if (!contact || !documentType) {
    return null
  }

  return (
    <Row>
      <Col className='d-flex flex-column zocks-gap-3'>
        <Row>
          <Col className='d-flex flex-column zocks-gap-0.25'>
            <h1>
              {documentLabel} - {getUserDisplayName(contact)}
            </h1>
            <div className='d-flex align-items-center zocks-gap-0.75'>
              <span className='paragraph-medium zocks-text-gray-700'> {documentType.name}</span>
              <div className='document-summary-header-divider rounded-circle zocks-bg-color-gray-700' />
              <span className='paragraph-medium zocks-text-gray-700'>{currentDateTime}</span>
            </div>
          </Col>
        </Row>
        <Row>
          <Col xs={7} className='d-flex flex-column gap-3'>
            <DocumentTypeChangedBadge />
            <DocumentSummaryEditor />
          </Col>
          <Col xs={5} className='d-flex flex-column gap-4'>
            {documentIsNotRelatedToClient ? <DocumentIsNotRelatedToClientBadge /> : null}
            <DocumentSummaryContacts />
            <DocumentSummaryChat />
          </Col>
        </Row>
      </Col>
    </Row>
  )
}

const DocumentIsNotRelatedToClientBadge = () => {
  const contact = useAppSelector(selectProcessDocumentContact)

  if (!contact) {
    return null
  }

  return (
    <div className='d-flex align-items-center gap-3 p-3 zocks-bg-color-warning rounded-3'>
      <i className='bi bi-exclamation-triangle fs-5' />
      <span className='paragraph-medium'>
        This document may not be related to {getUserDisplayName(contact)}. Review the summary and insights before syncing.
      </span>
    </div>
  )
}

const DocumentTypeChangedBadge = () => {
  const executionLoading = useAppSelector(selectDocumentSummaryExecutionLoading)
  const documentType = useAppSelector(selectDocumentType)
  const selectedDocumentType = useAppSelector(selectSelectedDocumentType)

  const { handleRerun } = useDocumentSummary()

  if (!documentType || !selectedDocumentType) {
    return null
  }

  if (documentType.systemDocumentTypeId === FALLBACK_DOCUMENT_TYPE_ID) {
    return (
      <div className='d-flex flex-column gap-3 p-3 zocks-bg-color-primary-light rounded-3'>
        <div className='d-flex gap-3'>
          <i className='bi bi-exclamation-circle fs-5' />
          <div className='d-flex flex-column gap-1'>
            <span className='paragraph-medium bold'>Get better results with a specific Document Type</span>
            <span className='paragraph-medium'>
              Your AI Assistant used a generic template because it couldn't find a match. Choose a document type below and rerun, or create a{' '}
              <Link to='/workspace/setup/document-type/document-type/create' target='_blank' className='zocks-text-primary text-decoration-underline'>
                custom Document Type
              </Link>
              .
            </span>
          </div>
        </div>
        <div className='d-flex align-items-center gap-3 zocks-ps-2.25'>
          <div className='document-summary-type-selector-container'>
            <DocumentTypeSelectDropdown />
          </div>
          <Button variant='primary' className='py-2 zocks-px-0.75' disabled={!selectedDocumentType} onClick={handleRerun} loading={executionLoading}>
            Rerun
          </Button>
        </div>
      </div>
    )
  }

  if (documentType.id !== selectedDocumentType.id) {
    return (
      <div className='d-flex align-items-center gap-3 p-3 zocks-bg-color-primary-light rounded-3'>
        <i className='bi bi-exclamation-circle fs-5' />
        <span className='paragraph-medium'>The document type has changed. Rerun processing to apply the new template.</span>
        <Button variant='primary' className='px-2 zocks-py-0.25' onClick={handleRerun} loading={executionLoading}>
          Rerun
        </Button>
      </div>
    )
  }

  return null
}
